import { Utils } from '@lightningjs/sdk';
import { StreamResolutions, StreamResolutionUtil } from '../utility/StreamResolutionUtil';
import { DetailTranslationKeys } from '../../api/models/TranslationKeys';
import { GlobalSettingsKeys } from '../../api/models/GlobalSettingsKeys';
import { CreditTypes, IconDef, ParentalRatings } from './detail.types';
import { getDayInfoString, getTime } from '../utility/DateFormatter';
import { formatQualityStringForPriceAndResolution } from '../utility/Formatters';
import { Backend } from '../../api/Backend';
import {
  IAssetStream,
  IBundleResolutionResponse,
  ICredit,
  IStreamPriceResponse3,
  IValidQuotePriceResponse3,
} from '../../api/models/AndromeTV.types';
import { AssetType } from '../../api/models/Shared.types';

const Constants = {
  allowedCredits: [CreditTypes.DIRECTOR, CreditTypes.ACTOR],
};

export class DetailDescriptionDataSource {
  protected contentType: string | undefined;
  protected contentId: string | undefined;
  protected backend: Backend;
  protected Constants: { commaSeparator: string; separator: string };
  constructor(contentId: string | undefined, contentType: string | undefined, backend: Backend) {
    this.contentType = contentType;
    this.contentId = contentId;
    this.backend = backend;
    this.Constants = {
      commaSeparator: ', ',
      separator: '  •  ',
    };
  }

  load() {
    throw new Error('Description method not implemented');
  }

  /**
   * get the icon matching the rating provided or undefined if there is none
   */
  getAgeratingIcon(rating: number | undefined) {
    if (rating === 0 || rating === undefined) {
      return undefined;
    }
    if (rating <= 4) {
      return Utils.asset('/images/agerating/age4.png');
    }
    if (rating <= 6) {
      return Utils.asset('images/agerating/age6.png');
    }
    if (rating <= 12) {
      return Utils.asset('images/agerating/age12.png');
    }
    if (rating <= 14) {
      return Utils.asset('images/agerating/age14.png');
    }
    if (rating <= 16) {
      return Utils.asset('images/agerating/age16.png');
    }
    if (rating <= 18) {
      return Utils.asset('images/agerating/age18.png');
    }
    return undefined;
  }

  getResolutionIconFromStreams(
    streams: IAssetStream[] | IBundleResolutionResponse[] | undefined,
    filterFull = false
  ): IconDef | undefined {
    if (!streams) return undefined;
    const resolution = StreamResolutionUtil.max(
      streams
        .filter(stream => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          return !filterFull || stream.type === 'FULL';
        })
        .map(stream => stream.resolution)
    );
    const icon = this.getResolutionIcon(resolution);
    return icon ? { src: icon } : undefined;
  }

  getResolutionIcon(resolution: string | undefined) {
    switch (resolution) {
      // case StreamResolutions.SD:
      case StreamResolutions.HD:
        return Utils.asset('/images/indicators/HD.png');
      case StreamResolutions.FOURK:
        return Utils.asset('/images/indicators/4K.png');
      default:
        return undefined;
    }
  }

  getMetaInformation(season: string | undefined, channel: string | undefined, channelPlayTime: string | undefined) {
    const items = [channelPlayTime, channel, season].filter(str => str && str !== '');
    return items.join(this.Constants.separator);
  }

  getFormattedSeasonInfo(season: number | undefined, episode: number | undefined) {
    const result = [];
    if (season !== undefined && season > 0) {
      const seasonStr = this.backend.translations.forKey(DetailTranslationKeys.detail_season);
      result.push(`${seasonStr} ${season}`);
    }
    if (episode !== undefined && episode > 0) {
      const epiStr = this.backend.translations.forKey(DetailTranslationKeys.detail_episode);
      result.push(`${epiStr} ${episode}`);
    }

    return result.length > 0 ? result.join(this.Constants.commaSeparator) : undefined;
  }

  /**
   *
   * @param {string[] | undefined} ratings
   * @returns {IconDef[]} icon paths
   */
  getParentalIcons(ratings: string[] | undefined): IconDef[] {
    const result: string[] = [];
    if (!ratings) return [];
    if (!this.backend.globalSettings.boolValue(GlobalSettingsKeys.showParentalIcons)) return [];
    ratings.forEach(rating => {
      rating = rating.toUpperCase();
      switch (rating) {
        case ParentalRatings.COARSELANGUAGE:
          result.push('images/detail/parentalcontrol/ic_parental_rating_coarse_language.png');
          break;
        case ParentalRatings.DISCRIMINATION:
          result.push('images/detail/parentalcontrol/ic_parental_rating_discrimination.png');
          break;
        case ParentalRatings.DRUGS:
          result.push('images/detail/parentalcontrol/ic_parental_rating_drugs.png');
          break;
        case ParentalRatings.FEAR:
          result.push('images/detail/parentalcontrol/ic_parental_rating_fear.png');
          break;
        case ParentalRatings.SEX:
          result.push('images/detail/parentalcontrol/ic_parental_rating_sex.png');
          break;
        case ParentalRatings.VIOLENCE:
          result.push('images/detail/parentalcontrol/ic_parental_rating_violence.png');
          break;
        default:
          break;
      }
    });
    return result.map(path => {
      return {
        src: Utils.asset(path),
      };
    });
  }

  filterCredits(credits: ICredit[] = []): Map<string, string> {
    const result = new Map();
    credits.forEach(credit => {
      if (Constants.allowedCredits.includes(credit.role)) {
        if (!result.has(credit.role)) {
          result.set(credit.role, {
            role: credit.role,
            names: [credit.name],
            roles: [credit.role],
          });
        } else {
          const obj = result.get(credit.role);
          obj.names.push(credit.name);
          obj.roles.push(credit.role);
        }
      }
    });
    return result;
  }

  getBookmarkPercentage(type: AssetType, id: string | undefined): Promise<number | undefined> {
    if (!id) return Promise.resolve(undefined);
    return this.backend.cachedBookmarkApi
      .bookmark(this.backend, {
        type: type,
        id: id,
      })
      .then(bookmark => {
        if (!!bookmark && bookmark.size > 0) {
          return bookmark.position / bookmark.size;
        }
        return undefined;
      });
  }

  getAvailableUntilFromQuote(backend: Backend, bundleQuote: IValidQuotePriceResponse3) {
    return (
      backend.translations.forKey(DetailTranslationKeys.detail_available_until) +
      ' ' +
      getDayInfoString(new Date(bundleQuote.endTime), false, backend) +
      ', ' +
      getTime(new Date(bundleQuote.endTime))
    );
  }

  getPricingInfo(streams: IStreamPriceResponse3[] | undefined): string {
    const result: string[] = [];
    streams
      ?.sort((a, b) => StreamResolutionUtil.toInt(b.resolution) - StreamResolutionUtil.toInt(a.resolution))
      .forEach(ottStream => {
        result.push(formatQualityStringForPriceAndResolution(this.backend, ottStream.price, ottStream.resolution));
      });
    return result.join('  •  ');
  }
}
